import * as UI from "@iyk/ui"
import * as React from "react"

import {
  Controls,
  FullscreenButton,
  MediaPlayer,
  MediaProvider,
  MuteButton,
  PlayButton,
  Poster,
  Time,
  TimeSlider,
  useMediaRemote,
  useMediaState,
  type MediaPlayerInstance,
  type MediaPlayerProps,
  type MediaPlayRequestEvent
} from "@vidstack/react"
import "@vidstack/react/player/styles/base.css"
import { FeedMediaController, useFeedMediaController } from "./FeedMediaController.tsx"

export const VideoPlayer = ({
  id,
  src,
  poster,
  size,
  fit,
  kind,
  className,
  pauseObserver,
  muted,
  ...props
}: VideoPlayer.Props) => {
  const videoPlayerRef = React.useRef<MediaPlayerInstance>(null)
  const player = useMediaRemote(videoPlayerRef)
  const pointerDownTime = React.useRef<number>(0)

  const isPlaying = useMediaState("playing", videoPlayerRef)
  const isMuted = useMediaState("muted", videoPlayerRef)

  const feedMediaController = useFeedMediaController(id, {
    play: () => player.play(),
    pause: () => player.pause(),
    mute: () => player.mute(),
    unmute: () => player.unmute()
  })

  const handlePointerDown = () => {
    pointerDownTime.current = Date.now()
  }

  const handleOverlayPlayRequest = (e: MediaPlayRequestEvent) => {
    const timeDiff = Date.now() - pointerDownTime.current
    if (timeDiff > 250) {
      e.preventDefault()
    }
  }

  React.useEffect(() => {
    if (isPlaying && !feedMediaController.isActive) {
      feedMediaController.playMedia(id)
    }
  }, [isPlaying, id])

  React.useEffect(() => {
    feedMediaController.setMuted(isMuted)
  }, [isMuted])

  return (
    <FeedMediaController.Observer
      id={id}
      className={classForPlayer({ size, fit, className })}
      paused={pauseObserver}
    >
      <MediaPlayer
        ref={videoPlayerRef}
        className="size-full"
        src={src}
        muted={muted ?? feedMediaController.globalMuteState}
        onPointerDown={handlePointerDown}
        {...props}
      >
        <MediaProvider className="size-full">
          {poster && (
            <PlayButton
              onMediaPlayRequest={handleOverlayPlayRequest}
              className={UI.cx(
                "absolute inset-0 z-30",
                "bg-background",
                "flex items-center justify-center",
                "media-started:hidden"
              )}
            >
              <Poster
                className={UI.cx(
                  "size-full object-contain",
                  "transition-opacity duration-300",
                  "data-[loading]:opacity-0 data-[error]:opacity-0 data-[hidden]:opacity-0"
                )}
                src={poster}
              />
              <span
                className={UI.classForCircleButton({
                  className: "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
                })}
              >
                <UI.Icon.Play />
              </span>
            </PlayButton>
          )}

          <Controls.Root
            className={UI.cx(
              "flex flex-col justify-end",
              "absolute inset-0 z-20",
              "transition-opacity duration-300",
              "data-[visible]:opacity-100 opacity-0"
            )}
          >
            {kind === "minimal" && <MinimalControlBar />}
            {kind === "full" && (
              <>
                <PlaybackToggleOverlay onMediaPlayRequest={handleOverlayPlayRequest} />
                <FullControlBar />
              </>
            )}
            {kind === "muteonly" && <UnmuteControlBar />}
          </Controls.Root>
        </MediaProvider>
      </MediaPlayer>
    </FeedMediaController.Observer>
  )
}

const PlaybackToggleOverlay = ({
  onMediaPlayRequest
}: {
  onMediaPlayRequest: (e: MediaPlayRequestEvent) => void
}) => {
  return (
    <Controls.Group className="grow flex">
      <PlayButton className="size-full outline-none" onMediaPlayRequest={onMediaPlayRequest} />
    </Controls.Group>
  )
}

const MinimalControlBar = () => {
  return (
    <Controls.Group
      className="shrink-0 w-full pb-2 px-2 flex items-center justify-end gap-1"
      data-disable-story-controls="true"
    >
      <PlayPauseButton />
      <MuteUnmuteButton />
    </Controls.Group>
  )
}

const UnmuteControlBar = () => {
  return (
    <Controls.Group
      className="shrink-0 w-full pb-2 px-2 flex items-center justify-end gap-1"
      data-disable-story-controls="true"
    >
      <MuteUnmuteButton />
    </Controls.Group>
  )
}

const FullControlBar = () => {
  return (
    <Controls.Group className="shrink-0 w-full pb-2 px-2">
      <div className="h-8 px-1.5 flex items-center bg-black/70 backdrop-blur-sm rounded">
        <PlayPauseButton />

        <Time className="text-[10px] tabular-nums tracking-tighter ml-1 mr-2" type="current" />

        <TimeSlider.Root
          noSwipeGesture
          className="grow group relative inline-flex h-8 w-full cursor-pointer touch-none select-none items-center outline-none aria-hidden:hidden"
        >
          <TimeSlider.Track className="relative h-0.5 w-full rounded-px bg-grayA-3 group-data-[focus]:ring-[2px]">
            <TimeSlider.TrackFill className="bg-white absolute h-full w-[var(--slider-fill)] rounded-px will-change-[width]" />
            <TimeSlider.Progress className="absolute z-10 h-full w-[var(--slider-progress)] rounded-px bg-grayA-4 will-change-[width]" />
          </TimeSlider.Track>
          <TimeSlider.Thumb className="absolute left-[var(--slider-fill)] top-1/2 z-20 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white ring-grayA-6 group-data-[dragging]:ring-2 will-change-[left]" />
        </TimeSlider.Root>

        <Time className="text-[10px] tabular-nums tracking-tighter mr-1 ml-2" type="duration" />

        <MuteUnmuteButton />
        <FullscreenOnOffButton />
      </div>
    </Controls.Group>
  )
}

const PlayPauseButton = () => (
  <PlayButton className={classForControlButton}>
    <UI.Icon.Play className="hidden media-paused:block" />
    <UI.Icon.Pause className="block media-paused:hidden" />
  </PlayButton>
)

const MuteUnmuteButton = () => (
  <MuteButton className={classForControlButton}>
    <UI.Icon.Mute className="hidden media-muted:block" />
    <UI.Icon.Unmute className="media-muted:hidden block" />
  </MuteButton>
)

const FullscreenOnOffButton = () => (
  <FullscreenButton className={classForControlButton}>
    <UI.Icon.FullscreenOn className="media-fullscreen:hidden" />
    <UI.Icon.FullscreenOff className="hidden media-fullscreen:block" />
  </FullscreenButton>
)

const classForPlayer = UI.cva(["relative [&_video]:size-full [&_video]:max-w-full [&_video]:max-h-full"], {
  variants: {
    size: {
      none: "",
      fill: "size-full"
    },
    fit: {
      cover: "[&_video]:object-cover",
      contain: "[&_video]:object-contain"
    }
  }
})

const classForControlButton = UI.cx(
  "shrink-0 group rounded size-6 hover:bg-grayA-3 transition-all duration-150 flex items-center justify-center"
)

export const VideoPlayerHooks = { useMediaState, useMediaRemote }

export namespace VideoPlayer {
  export type Props = Omit<MediaPlayerProps, "children" | "onPointerDown"> & {
    id: string
    poster?: string
    size: "none" | "fill"
    fit?: "cover" | "contain"
    kind: "minimal" | "full" | "muteonly"
    pauseObserver?: boolean
  }

  export type Instance = MediaPlayerInstance
}
